import React, { useState, useEffect } from "react";
import { useCompany } from "../../Context/CompanyContext";
import { fetchWithAuth } from "../../Context/UserContext";
import API_URL from "../../config";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";

const Contacto = () => {
  const { companies, fetchCompanies } = useCompany();
  const [editFormData, setEditFormData] = useState({
    pagina_web: "",
    telefono_empresa: "", // Agregado aquí
    informacion_contacto: [],
    redes_sociales: [],
  });

  const [newContact, setNewContact] = useState({
    nombre: "",
    telefono: "",
    email: "",
  });
  const [newSocial, setNewSocial] = useState({ url: "" });
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    if (companies?.company) {
      setEditFormData({
        pagina_web: companies?.profile?.pagina_web || "",
        telefono_empresa: companies?.profile?.telefono_empresa || "", // Agregado aquí
        informacion_contacto: Array.isArray(
          companies?.profile?.informacion_contacto
        )
          ? companies.profile.informacion_contacto
          : [],
        redes_sociales: Array.isArray(companies?.profile?.redes_sociales)
          ? companies?.profile?.redes_sociales
          : [],
      });
    }
  }, [companies]);

  const id_company = companies.company.id_company;

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    if (field === "pagina_web") {
      const webPattern = /\.(com|org|net|edu|gov)(\.[a-z]{2})?$/i;
      if (!webPattern.test(value)) {
        setPageError(
          "El formato de la página web no es válido. Ejemplo: www.ejemplo.com"
        );
      } else {
        setPageError("");
      }
    }

    setEditFormData({
      ...editFormData,
      [field]: value,
    });
  };

  const handleSaveContacto = async () => {
    if (pageError) {
      toast.error("Por favor, soluciona el error antes de guardar.");
      return;
    }

    // Crear objeto solo con los datos modificados
    let updatedData = {};

    if (editFormData.pagina_web.trim() !== "") {
      updatedData.pagina_web = editFormData.pagina_web;
    }

    if (editFormData.telefono_empresa.trim() !== "") {
      updatedData.telefono_empresa = editFormData.telefono_empresa;
    }

    if (editFormData.informacion_contacto.length > 0) {
      updatedData.informacion_contacto = editFormData.informacion_contacto;
    }

    if (editFormData.redes_sociales.length > 0) {
      updatedData.redes_sociales = editFormData.redes_sociales;
    }

    // Si no hay cambios, no hacemos la petición
    if (Object.keys(updatedData).length === 0) {
      toast.error("No hay cambios para guardar.");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company-profile/${id_company}/update-contacto`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ profile: updatedData }),
        }
      );

      if (response.ok) {
        toast.success("Información de contacto actualizada exitosamente.");
        fetchCompanies(); // Actualizar datos en el frontend
        setPageError("");
        setError("");
      } else {
        const data = await response.json();
        toast.error(`Error al actualizar contacto: ${data.error}`);
      }
    } catch (error) {
      console.error("Error en la actualización de contacto:", error);
      toast.error("Error al actualizar contacto.");
    }
  };

  const handleAddContact = () => {
    if (!newContact.nombre || !newContact.telefono || !newContact.email) {
      setError("Por favor, completa todos los campos.");
      return;
    }

    if (!/^\+?[\d\s()-]{9,}$/.test(newContact.telefono)) {
      setError(
        "El teléfono debe contener al menos 9 dígitos y puede incluir caracteres como +, (), o espacios."
      );
      return;
    }

    if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(newContact.email)) {
      setError("El formato del correo no es válido. Debe ser correo@email.com");
      return;
    }

    const currentContacts = Array.isArray(editFormData.informacion_contacto)
      ? editFormData.informacion_contacto
      : [];

    setEditFormData({
      ...editFormData,
      informacion_contacto: [...currentContacts, newContact],
    });

    setNewContact({ nombre: "", telefono: "", email: "" });
    // ✅ Mostrar la información del contacto agregado en el alert
    toast.success(
      `Contacto agregado correctamente:\n\n📛 Nombre: ${newContact.nombre}\n📞 Teléfono: ${newContact.telefono}\n📧 Email: ${newContact.email}`
    );

    setError("");
  };

  const handleAddSocial = () => {
    if (!newSocial.url) {
      setError("Por favor, proporciona una URL válida.");
      return;
    }

    setEditFormData((prevFormData) => ({
      ...prevFormData,
      redes_sociales: [...prevFormData.redes_sociales, newSocial],
    }));

    // ✅ Mostrar información con iconos en el alert
    toast.success(`🔗 Nueva Red Social Agregada:\n\n🌐 URL: ${newSocial.url}`);

    setNewSocial({ url: "" });
    setError("");
  };

  const handleRemoveContact = (index) => {
    const updatedContacts = editFormData.informacion_contacto.filter(
      (_, i) => i !== index
    );
    setEditFormData({ ...editFormData, informacion_contacto: updatedContacts });
  };

  const handleRemoveSocial = (index) => {
    setEditFormData((prevFormData) => ({
      ...prevFormData,
      redes_sociales: prevFormData.redes_sociales.filter((_, i) => i !== index),
    }));
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <h3 className="text-lg font-semibold text-foreground">Datos de Contacto</h3>
        {pageError && <p className="text-sm text-destructive">{pageError}</p>}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Página Web:</label>
          <Input
            type="text"
            placeholder="Ejemplo: www.miempresa.com"
            value={editFormData?.pagina_web || ""}
            onChange={(e) => handleInputChange(e, "pagina_web")}
            maxLength={2048} // Límite estándar para URLs
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Teléfono de la Empresa:</label>
          <Input
            type="text"
            placeholder="Ejemplo: +51 987 654 321"
            value={editFormData.telefono_empresa || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9()+-]/g, ""); // Permite solo números, +, -, y ()
              if (value.length <= 20) {
                setEditFormData({ ...editFormData, telefono_empresa: value });
              }
            }}
            maxLength={20} // Límite de caracteres
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Información de Contacto:</label>
          <div className="grid gap-2 sm:grid-cols-3">
            <Input
              type="text"
              placeholder="Nombre de contacto"
              value={newContact.nombre}
              onChange={(e) => setNewContact({ ...newContact, nombre: e.target.value })}
              maxLength={50}
            />
            <Input
              type="text"
              placeholder="Teléfono (9-20 cifras): 987654321"
              value={newContact.telefono}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9()+-]/g, ""); // Permite solo números, +, -, y ()
                if (value.length <= 20) {
                  setNewContact({ ...newContact, telefono: value });
                }
              }}
              maxLength={20}
            />
            <Input
              type="email"
              placeholder="Email@email.com"
              value={newContact.email}
              onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
              maxLength={50}
            />
          </div>
          <Button type="button" variant="outline" className="mt-2" onClick={handleAddContact}>
            Agregar
          </Button>
          {error && <p className="mt-1 text-sm text-destructive">{error}</p>}

          <ul className="mt-2 space-y-1">
            {Array.isArray(editFormData.informacion_contacto) && editFormData.informacion_contacto.length > 0 ? (
              editFormData.informacion_contacto.map((contacto, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  {`${contacto.nombre}: ${contacto.telefono} | ${contacto.email}`}
                  <button onClick={() => handleRemoveContact(index)} className="hover:text-destructive">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))
            ) : (
              <li className="text-sm text-muted-foreground">
                <i>No hay información de contacto seleccionada.</i>
              </li>
            )}
          </ul>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Redes Sociales:</label>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="Ejemplo: https://www.facebook.com/miPerfil"
              value={newSocial.url}
              onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
              maxLength={2000}
            />
            <Button type="button" variant="outline" onClick={handleAddSocial}>
              Agregar
            </Button>
          </div>

          <ul className="mt-2 space-y-1">
            {editFormData.redes_sociales.length > 0 ? (
              editFormData.redes_sociales.map((social, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  {social.url}
                  <button onClick={() => handleRemoveSocial(index)} className="hover:text-destructive">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))
            ) : (
              <li className="text-sm text-muted-foreground">
                <i>No hay redes sociales seleccionadas.</i>
              </li>
            )}
          </ul>
        </div>

        <Button onClick={handleSaveContacto}>Guardar Cambios</Button>
      </CardContent>
    </Card>
  );
};

export default Contacto;
